import React, { useState } from 'react';
import { Input } from 'antd';
import { Form, Formik } from 'formik';
import Modal from '../../../components/Modal';
import { useActions } from '../../../context/Actions.context';
import { useQueryClient } from 'react-query';
import { useMuteAuthor } from '../../../core/modules/book/hooks/useMuteAuthor';
import { useUnmuteAuthor } from '../../../core/modules/book/hooks/useUnmuteAuthor';
import { Title } from '../../../components/styles';
import { MUTEAUTHOR, SOURCE, UNMUTEAUTHOR } from '../../../core/constants';
import { ReactComponent as UnmuteIcon } from '../../../assets/icons/bell-off-fill-unmute.svg';
import { ReactComponent as MuteIcon } from '../../../assets/icons/bell-off-outline-mute.svg';
import { useHistory } from 'react-router-dom';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

const { TextArea } = Input;

interface IAuthorProps {
  ASIN: string;
  Author?: string;
}

interface IProps {
  book: IAuthorProps;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const MuteAuthorTitles: React.FC<IProps> = ({
  book,
  modal,
  toggleModal,
  id
}) => {
  const { Author } = book;
  const { isActionLoading, setActionLoading, source: srcPath } = useActions();
  const { mutateAsync: muteAsync } = useMuteAuthor(MUTEAUTHOR);
  const { mutateAsync: unmuteAsync } = useUnmuteAuthor(UNMUTEAUTHOR);
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const history = useHistory();

  const handleClose = (resetForm?: Function) => {
    setNote('');
    resetForm?.();
    toggleModal();
  };

  const onMute = (_values, cb) => {
    if (isActionLoading) {
      return;
    }
    setActionLoading(true);

    try {
      const payload = {
        authors: [book.Author],
        note,
        duration: {
          'value': 6,
          'unit': 'months'
        }
      };

      const promise = muteAsync(payload);

      cb.resetForm();

      const capturedSource = srcPath['source'];
      const capturedAuthor = book.Author;

      promise.then(() => {
        showActionNotification({
          type: 'success',
          message: 'Author for the title added to muted authors list successfully.',
          destination: {
            name: 'Muted Authors',
            onGoTo: () => history.push(`${capturedSource}/muted-authors`)
          },
          onUndo: async () => {
            await (unmuteAsync as Function)({ authors: [capturedAuthor] });
          },
          queryClient,
          id
        });
      });

      promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
      promise.then(() => queryClient.refetchQueries(`get-${id}`));
      promise.then(() => {
        handleClose(cb.resetForm);
      });
      promise.finally(() => setActionLoading(false));
    } catch (error) {
      handleClose(cb.resetForm);

      showActionNotification({
        type: 'error',
        message: 'Failed to mute author'
      });

      setActionLoading(false);

      console.error('Failed to mute author', {
        author: Author,
        error
      });
    }
  };

  return (
    <Formik
      initialValues={{}}
      enableReinitialize={true}
      onSubmit={onMute}
    >
      {({ handleSubmit, resetForm }) => (
        <Form>
          <Modal
            title='Mute Author'
            visible={modal}
            onOk={handleSubmit}
            okText='Mute Author'
            onCancel={() => handleClose(resetForm)}
            confirmLoading={isActionLoading}
            okButtonProps={{ disabled: isActionLoading }}
          >
            <Title>
              <p style={{ fontWeight: 500 }}>
                Following author will be muted {' '}
                <MuteIcon
                  className='actions-icons actions-icons-mute'
                  style={{ paddingTop: '5px' }}
                /> for 6 months:
              </p>

              <br/>

              <p style={{ marginBottom: 18 }}>{Author}</p>

              <ul style={{ paddingLeft: 18, marginBottom: 16 }}>
                <li>When muted, you won&apos;t see any titles from the author in
                  { srcPath['source'] === SOURCE.ao3 ? ' Popular' : ' All New Titles or Hot Titles' }.</li>
                <li>
                  All the titles from snoozed authors will be available under {' '}
                  <b>Muted Authors</b> tab.
                </li>
                <li>
                  You can manually un-snooze any author before the 6-month period by {' '}
                  clicking on <UnmuteIcon
                    className='actions-icons actions-icons-unmute'
                    style={{ paddingTop: '5px' }} /> {' '}
                  <b>Unmute</b> under Actions.
                </li>
                <li>
                  After the mute period is over, these authors&apos; titles will be visible
                  under {srcPath['source'] === SOURCE.ao3 ? 'Popular ' : 'All New Titles or Hot Titles '}
                  if their ranking criteria is met.
                </li>
              </ul>
            </Title>

            <TextArea
              placeholder='Leave a note stating the reason behind muting author(s)'
              maxLength={400}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
            />

            <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
              {note.length} / 400
            </div>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default React.memo(MuteAuthorTitles);